import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Available background images
const BACKGROUND_IMAGES = [
  '/assets/images/backgrounds/shoes-01.jpg',
  '/assets/images/backgrounds/shoes-02.jpg',
  '/assets/images/backgrounds/shoes-03.jpg',
  '/assets/images/backgrounds/shoes-04.jpg',
  '/assets/images/backgrounds/shoes-05.jpg',
  '/assets/images/backgrounds/shoes-06.jpg',
];

// Default background image (the original one)
const DEFAULT_BACKGROUND = '/assets/images/backgrounds/cropped-cropped-shoes-7.jpg';

/**
 * Hook to manage dynamic background images based on navigation
 * Changes background image randomly when navigating between sections
 */
export const useBackgroundImage = () => {
  const location = useLocation();
  const [backgroundImage, setBackgroundImage] = useState<string>(DEFAULT_BACKGROUND);
  const [previousPath, setPreviousPath] = useState<string>('');

  // Function to get a random background image (excluding current one)
  const getRandomBackground = (currentBackground: string): string => {
    const availableImages = BACKGROUND_IMAGES.filter(img => img !== currentBackground);
    const randomIndex = Math.floor(Math.random() * availableImages.length);
    return availableImages[randomIndex];
  };

  // Function to determine if we should change background
  const shouldChangeBackground = (currentPath: string, prevPath: string): boolean => {
    // Don't change on initial load
    if (!prevPath) return false;
    
    // Get the main section from paths
    const getCurrentSection = (path: string): string => {
      if (path === '/') return 'home';
      const segments = path.split('/').filter(Boolean);
      return segments[0] || 'home';
    };

    const currentSection = getCurrentSection(currentPath);
    const previousSection = getCurrentSection(prevPath);
    
    // Change background when switching between different main sections
    return currentSection !== previousSection;
  };

  useEffect(() => {
    const currentPath = location.pathname;
    
    if (shouldChangeBackground(currentPath, previousPath)) {
      const newBackground = getRandomBackground(backgroundImage);
      setBackgroundImage(newBackground);
      
      // Optional: Add a subtle transition effect
      console.log(`🎨 Background changed from ${backgroundImage.split('/').pop()} to ${newBackground.split('/').pop()}`);
    }
    
    setPreviousPath(currentPath);
  }, [location.pathname, backgroundImage, previousPath]);

  // Function to manually change background (for testing or other uses)
  const changeBackground = () => {
    const newBackground = getRandomBackground(backgroundImage);
    setBackgroundImage(newBackground);
  };

  return {
    backgroundImage,
    changeBackground,
    availableImages: BACKGROUND_IMAGES,
  };
};

export default useBackgroundImage;
